import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Input } from '@angular/core';
import { WorkspaceStore } from '../../../services/stores/workspace.store';
import { BaseComponent } from '../../base/base.component';
import { INodeInstanceModel } from '../../../models/node/node-instance.model';
import { IWorkspaceInstanceModel } from '../../../models/workspace/workspace-instance.model';
import { DashboardHelper } from '../../../services/dashboard.service';
import { BlueprintDataHelper } from '../../../services/helpers/blueprint-data.helper';
import { IStatusGraphMetadata, IStatusOption } from '../../../models/blueprint/blueprint-field-dropdown-single.model';
import { BlueprintStore } from '../../../services/stores/blueprint.store';
import { STEP_STATUS_DISPLAY_TO_DO } from '../../../modules/catch-core/data/constants/app.constants';
import { FIELD_NAME_STATUS_LOWER } from '../../../modules/catch-core/data/constants/blueprint.constants';

interface IStatusGraphSegment {
  key: string;
  statusText: string;
  statusCategory: string;
  percentage: string;
  color: string;
  order: number;
}

@Component({
  selector: 'app-status-graph',
  templateUrl: './status-graph.component.html',
  styleUrls: ['./status-graph.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StatusGraphComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() width: string = '300px';
  @Input() height: string = '20px';
  @Input() applySpaceClass: boolean = true;

  public progressSegments: IStatusGraphSegment[] = [];

  private selectedWorkspace: IWorkspaceInstanceModel = {} as IWorkspaceInstanceModel;
  private workspaceNodes: Array<INodeInstanceModel> | any[] = [];

  private totalScripts = 0;

  constructor(
    public dashboardHelper: DashboardHelper,
    private workspaceStore: WorkspaceStore,
    private blueprintDataHelper: BlueprintDataHelper,
    private blueprintStore: BlueprintStore,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribers.selectedWorkspace = this.workspaceStore.$selectedWorkspaceInstance.subscribe((data) => {
      if (data?.bluePrintId) {
        this.blueprintStore.ensureBlueprintLoaded(data.bluePrintId);
      }

      this.initializeWorkspaceData(data);
    });

    this.subscribers.blueprintLoaded = this.blueprintStore.$selectedBluePrint.subscribe((blueprint) => {
      const workspace = this.workspaceStore.selectedWorkspaceInstance;
      if (!workspace?.bluePrintId || !blueprint || blueprint.id !== workspace.bluePrintId) {
        return;
      }

      this.initializeWorkspaceData(workspace);
    });

    this.subscribers.allBlueprints = this.blueprintStore.$allBluePrints.subscribe(() => {
      const workspace = this.workspaceStore.selectedWorkspaceInstance;
      if (!workspace?.bluePrintId) {
        return;
      }

      this.initializeWorkspaceData(workspace);
    });

    this.subscribers.patchedEdgeInstance = this.workspaceStore.$patchedEdgeInstance.subscribe((res: any) => {
      if (res) {
        this.loadNodesForWorkspace();
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private initializeWorkspaceData(workspace: IWorkspaceInstanceModel | null | undefined): void {
    if (!workspace?.bluePrintId) {
      return;
    }

    const blueprint = this.blueprintStore.getBlueprintByIdFromCache(workspace.bluePrintId);
    if (!this.blueprintStore.isBlueprintHydrated(blueprint)) {
      this.blueprintStore.ensureBlueprintLoaded(workspace.bluePrintId);
      return;
    }

    this.selectedWorkspace = workspace;
    this.loadNodesForWorkspace();
    this.cdr.detectChanges();
  }

  loadNodesForWorkspace() {
    if (this.selectedWorkspace) {
      this.workspaceNodes = this.selectedWorkspace.nodeInstances || [];
      this.updateProgressBar();
    }
  }

  private updateProgressBar(): void {
    this.totalScripts = 0;

    const statusFieldIdMap = this.blueprintDataHelper.getStatusFieldIdByNodeTypeId();
    const legacyStatusMetadata = this.blueprintDataHelper.getStatusGraphMetadata();
    const statusCounts = new Map<string, number>();
    const segmentMetadata = new Map<string, IStatusGraphMetadata>();
    // Per-call memo of nodeTypeId → statusOptions to avoid repeated findFieldByCategory work.
    const statusOptionsByNodeTypeId = new Map<string, IStatusOption[]>();

    if (this.workspaceNodes) {
      for (const node of this.workspaceNodes) {
        if (!node?.fields) continue;

        const nodeTypeId = node.nodeTypeId ?? '';
        const statusFieldId = statusFieldIdMap.get(nodeTypeId);
        if (!statusFieldId) continue;

        const rawStatus = node.fields[statusFieldId];
        const rawText = typeof rawStatus === 'string' ? rawStatus.trim() : '';
        if (!rawText) continue;

        this.totalScripts++;

        // Resolve raw value (post-Phase-E: a status option id; legacy: a status text) via the
        // node-type's statusOptions. This keys aggregation off option.id when available so two
        // blueprint statuses sharing display text never collide.
        const statusOptions = this.getStatusOptionsForNodeType(nodeTypeId, statusOptionsByNodeTypeId);
        const option = this.blueprintDataHelper.resolveStatusOption(statusOptions, rawText);

        let key: string;
        let statusText: string;
        let statusCategory: string;
        let color: string;
        let order: number;

        if (option) {
          // Canonical id-keyed aggregation.
          key = option.id || option.statusText.toLowerCase();
          statusText = option.statusText;
          statusCategory = option.statusCategory && option.statusCategory.length > 0 ? option.statusCategory : this.blueprintDataHelper.inferStatusCategory(statusText);
          color = this.blueprintDataHelper.resolveGraphVisibleColor(option, statusText);
          order = option.order ?? 0;
        } else {
          // Legacy fallback: raw value isn't in this node-type's options. Try the global
          // text-keyed metadata, otherwise merge into the first blueprint status sharing the
          // same inferred category so legacy "To Do" values still group sensibly.
          const legacyKey = legacyStatusMetadata.has(rawText.toLowerCase()) ? rawText.toLowerCase() : this.findCanonicalStatusKey(rawText, legacyStatusMetadata);
          const legacy = legacyStatusMetadata.get(legacyKey);
          key = legacyKey;
          statusText = legacy?.statusText ?? rawText;
          statusCategory = legacy?.statusCategory ?? this.blueprintDataHelper.inferStatusCategory(rawText);
          color = legacy?.color ?? '#6c7481';
          order = legacy?.order ?? 0;
        }

        statusCounts.set(key, (statusCounts.get(key) ?? 0) + 1);
        if (!segmentMetadata.has(key)) {
          segmentMetadata.set(key, { statusText, statusCategory, color, order });
        }
      }
    }

    this.progressSegments = this.buildProgressSegments(statusCounts, segmentMetadata);

    this.cdr.detectChanges();
  }

  private getStatusOptionsForNodeType(nodeTypeId: string, memo: Map<string, IStatusOption[]>): IStatusOption[] {
    if (memo.has(nodeTypeId)) {
      return memo.get(nodeTypeId) ?? [];
    }
    const nodeTypeName = this.blueprintDataHelper.getNodeTypeNameById(nodeTypeId);
    const statusField = this.blueprintDataHelper.findFieldByCategory(nodeTypeName ?? '', FIELD_NAME_STATUS_LOWER);
    const options: IStatusOption[] = (statusField?.value as any)?.statusOptions ?? (statusField?.value as any)?.StatusOptions ?? [];
    memo.set(nodeTypeId, options);
    return options;
  }

  private buildProgressSegments(statusCounts: Map<string, number>, metadata: Map<string, IStatusGraphMetadata>): IStatusGraphSegment[] {
    if (this.totalScripts <= 0) {
      return [];
    }

    return Array.from(statusCounts.entries())
      .filter(([_, count]) => count > 0)
      .map(([key, count]) => {
        const meta = metadata.get(key);
        const pct = (count / this.totalScripts) * 100;
        return {
          key,
          statusText: meta?.statusText ?? key,
          statusCategory: meta?.statusCategory ?? STEP_STATUS_DISPLAY_TO_DO,
          percentage: pct + '%',
          color: meta?.color ?? '#6c7481',
          order: meta?.order ?? 0,
        };
      })
      .filter((segment) => parseFloat(segment.percentage) > 0)
      .sort((a, b) => {
        const categoryDifference = this.blueprintDataHelper.getStatusCategoryRank(a.statusCategory) - this.blueprintDataHelper.getStatusCategoryRank(b.statusCategory);
        if (categoryDifference !== 0) {
          return categoryDifference;
        }
        return a.order - b.order || a.statusText.localeCompare(b.statusText);
      });
  }

  /**
   * When a node's status value is not in the current blueprint's statusOptions (e.g. legacy "To Do"),
   * find the first blueprint status that shares the same inferred category and merge into that.
   */
  private findCanonicalStatusKey(statusText: string, metadata: Map<string, IStatusGraphMetadata>): string {
    if (!statusText || !metadata || metadata.size === 0) return (statusText ?? '').toLowerCase();
    const inferredCategory = this.blueprintDataHelper.inferStatusCategory(statusText);
    for (const [key, meta] of metadata) {
      if (meta?.statusCategory === inferredCategory) {
        return key;
      }
    }
    return statusText.toLowerCase();
  }

  getRoundedPercentage(perc: string) {
    if (!perc) {
      return;
    }

    const percentIndex = perc.indexOf('%');
    if (percentIndex > 0) {
      const numValue = parseFloat(perc.substring(0, percentIndex));
      const rounded = Math.round(numValue);
      return (rounded === 0 && numValue > 0 ? 1 : rounded) + '%';
    }

    return perc;
  }
}
